import {
  CircularProgress,
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps,
  Tooltip,
  type TooltipProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";

export interface IconButtonProps extends MuiIconButtonProps {
  "aria-label": string;
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "quaternary"
    | "colored"
    | "coloredMain"
    | "error"
    | "white"
    | "light"
    | "warning";
  href?: string;
  size?: "small" | "medium" | "large";
  loading?: boolean;
  tooltip?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  rounded?: boolean;
}

const extraProps: PropertyKey[] = ["rounded", "variant"];

const StyledIconButton = styled(MuiIconButton, {
  shouldForwardProp: (prop) => !extraProps.includes(prop),
})<IconButtonProps>(({ variant, rounded, theme }) => {
  const variantStyles = {
    light: {
      backgroundColor: theme.palette.secondary[4],

      "&:hover": {
        backgroundColor: theme.palette.secondary["3/50"],
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.secondary[4],
      },
    },
    primary: {
      borderColor: theme.palette.secondary[4],
      backgroundColor: theme.palette.secondary[5],

      "&.Mui-disabled": {
        backgroundColor: theme.palette.secondary[4],
      },

      "&:hover": {
        borderColor: theme.palette.secondary[3],
        backgroundColor: theme.palette.secondary[4],
      },
    },
    secondary: {
      borderColor: theme.palette.secondary[5],
      backgroundColor: theme.palette.secondary[6],

      "&:hover": {
        borderColor: theme.palette.secondary[4],
        backgroundColor: theme.palette.secondary[5],
      },
    },
    tertiary: {
      borderColor: theme.palette.secondary[6],
      backgroundColor: "transparent",

      "&:hover": {
        borderColor: theme.palette.secondary[5],
        backgroundColor: "transparent",
      },
    },
    quaternary: {
      borderColor: "transparent",
      backgroundColor: "transparent",

      "&:hover": {
        backgroundColor: theme.palette.secondary["5/25"],
      },
    },
    colored: {
      backgroundColor: theme.palette.primary.light,
      "&.Mui-disabled": {
        backgroundColor: theme.palette.primary.light,
      },
      "&:hover": {
        backgroundColor: theme.palette.primary.light,
      },
    },
    coloredMain: {
      backgroundColor: theme.palette.primary.main,
      "&.Mui-disabled": {
        backgroundColor: theme.palette.primary.main,
      },
      "&:hover": {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.common.black,
      },
    },
    error: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
      "&.Mui-disabled": {
        backgroundColor: theme.palette.primary.light,
      },
      "&:hover": {
        backgroundColor: theme.palette.error.main,
      },
    },
    white: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,

      "&.Mui-disabled": {
        backgroundColor: theme.palette.secondary[2],
        color: theme.palette.secondary[6],
      },

      "&:hover": {
        backgroundColor: theme.palette.secondary[2],
      },
    },
    warning: {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.common.black,
      "&:hover": {
        backgroundColor: theme.palette.warning.light,
      },
    },
  };

  const styles = variant ? variantStyles[variant] : {};

  if (rounded) {
    Object.assign(styles, {
      borderRadius: "50%",
    });
  }

  return styles;
});

const LoaderWrapper = styled("span")({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
});

// TODO: Review this component, the default `color` props is broken + `variant = "warning"` instead of `color="warning"`
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      size = "medium",
      variant = "primary",
      rounded = false,
      tooltip = true,
      tooltipProps,
      disabled = false,
      loading = false,
      children,
      ...rest
    }: IconButtonProps,
    ref,
  ) => {
    const buttonEl = (
      <StyledIconButton
        {...rest}
        size={size}
        variant={variant}
        rounded={rounded}
        ref={ref}
        disabled={loading || disabled}
      >
        {loading && (
          <LoaderWrapper>
            <CircularProgress size={20} />
          </LoaderWrapper>
        )}
        {!loading && children}
      </StyledIconButton>
    );

    if (tooltip) {
      return (
        <Tooltip title={rest["aria-label"]} {...tooltipProps} enterDelay={300}>
          {/* Wrap the button in a span to show a tooltip even if the button is disabled */}
          <span>{buttonEl}</span>
        </Tooltip>
      );
    }

    return buttonEl;
  },
);
